import { AudioEngine } from "@/audio/core/AudioBackend";
import type { AudioEngineState, PlaybackHandle } from "@/audio/core/AudioBackend";
import { SequenceScheduler } from "@/audio/core/Scheduler";
import type { CandidateLayer, SoundEventId, SynthLayer } from "@/audio/core/types";
import { FINAL_MAPPING, buildLevelSummaryAudio } from "@/audio/catalog";
import type { RecordCombo } from "@/audio/catalog";
import { AudioPreferences } from "@/audio/AudioPreferences";
import type { BiteFeedbackEvent } from "@/core/types";
import type { BiteBonusResult, SkillMoment } from "@/core/scoring/types";

/** The subset of `AudioEngine`'s public surface `AudioManager` depends on - lets tests inject a fake backend instead of touching a real (unavailable-in-Node) `AudioContext`. */
export interface AudioBackendLike {
  getState(): AudioEngineState;
  onStateChange(cb: (state: AudioEngineState) => void): () => void;
  unlock(): void;
  suspend(): void;
  playRecipe(recipe: SynthLayer, options?: { transposeSemitones?: number }): PlaybackHandle;
  stopAll(): void;
  destroy(): void;
}

/** Narrow context `onBiteEvent` needs from the same authoritative signal `GameScene` already computes for the visual layer (`ctx.wormholeAlreadyActiveBefore`) - deliberately not the full, Phaser-shaped `BiteImpactContext`, so `AudioManager` never depends on anything visual-specific. */
export interface BiteAudioContext {
  readonly wormholeAlreadyActiveBefore?: boolean;
}

/** Everything `update()` needs from the current authoritative snapshot - three plain nullable numbers, never the concrete `LevelGameSnapshot` type, so `AudioManager` stays decoupled from `LevelGame`'s full shape. */
export interface AudioTimedSnapshot {
  readonly wormholeRemainingMs: number | null;
  readonly speedTrialRemainingMs: number | null;
  readonly starfruitGateRemainingMs: number | null;
}

/** Priority tiers per the production-integration spec's "mixing and priority" section - highest first. Used only to decide which currently-playing one-shot gets interrupted when polyphony is capped. */
export const AUDIO_PRIORITY = {
  runEnd: 100,
  majorEvent: 90,
  statusActivation: 80,
  skill: 60,
  rush: 40,
  ambient: 20,
} as const;

const MAX_POLYPHONY = 6;
/** The STARFRUIT gate's own countdown still uses the full 3-2-1 warning ladder - never touched by either QA follow-up below. */
const WARNING_TIERS_MS = [3000, 2000, 1000] as const;
/**
 * QA follow-up (WORMHOLE, then SPEED TRIAL audio simplification): both
 * WORMHOLE and SPEED TRIAL get exactly ONE expiry/final warning - never
 * the shared 3-2-1 ladder above - fired on crossing 2 seconds remaining.
 * The visual countdown badges are untouched; this only affects which
 * audio tier list `checkWarningTier()` is given for these two families.
 */
const SINGLE_EXPIRY_WARNING_TIER_MS = [2000] as const;

interface ActiveOneShot {
  scheduler: SequenceScheduler;
  priority: number;
}

/** `beforeMs`/`afterMs` are each just a nullable remaining-time reading - a generic "did this countdown just start/refresh/keep ticking/end" classifier reused for WORMHOLE and SPEED TRIAL alike (the same rule `src/scenes/feedback/mapEvents.ts`'s `classifyWormholeTransition` already uses for the visual layer, inlined here so `AudioManager` never depends on that module). */
function classifyCountdown(beforeMs: number | null, afterMs: number | null): "started" | "refreshed" | "ticking" | "ended" | "none" {
  if (beforeMs === null && afterMs !== null) return "started";
  if (beforeMs !== null && afterMs === null) return "ended";
  if (beforeMs !== null && afterMs !== null) return afterMs > beforeMs ? "refreshed" : "ticking";
  return "none";
}

/**
 * The single production-owned audio orchestrator (PR13-D production
 * integration): one `AudioEngine`/`AudioContext`, the approved
 * `FINAL_MAPPING`, and every dedupe/priority/polyphony/loop-lifecycle rule
 * needed to play gameplay sound safely. `GameScene` calls the `onXxx`
 * methods here from the exact same authoritative transition points it
 * already calls the matching `VisualFeedbackController` method from - this
 * class never re-derives an event from anything else, and never imports
 * `VisualFeedbackController` itself, so visual and audio consume the same
 * authoritative signal without one depending on the other.
 *
 * A module-level singleton (`getAudioManager()`), never recreated per
 * Scene, so its unlock state/preferences survive a `scene.restart()` the
 * same way a real Sound On/Off setting must. Deliberately owns no
 * repeating/looping playback at all (QA follow-up: both WORMHOLE and
 * SPEED TRIAL were found too repetitive/annoying while merely active and
 * are now one-shot-only) - every gameplay sound here is a single
 * scheduled multi-layer moment at most, never a self-rescheduling loop.
 */
export class AudioManager {
  private soundEnabled: boolean;
  private paused = false;
  private activeOneShots: ActiveOneShot[] = [];
  private pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  private prevWormholeRemainingMs: number | null = null;
  private prevSpeedTrialRemainingMs: number | null = null;
  private speedTrialEndedHandled = false;
  private wormholeWarnTierMs: number | null = null;
  private speedTrialWarnTierMs: number | null = null;
  private starfruitGateWarnTierMs: number | null = null;
  private starfruitPresent = false;
  private prevFlowStreak = 0;

  private lastEventId: SoundEventId | null = null;

  constructor(private readonly backend: AudioBackendLike = new AudioEngine()) {
    this.soundEnabled = AudioPreferences.isSoundEnabled();
    // Page-visibility safety (spec section 8): a hidden tab suspends the
    // context outright (silencing any loop still ticking in the
    // background) and drops every pending one-shot so nothing stale plays
    // late; becoming visible again just resumes the same context - no new
    // gesture is required to resume an already-unlocked one. Guarded for
    // Node test environments, which have no `document`.
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.clearPendingOneShots();
      this.backend.suspend();
    } else {
      this.backend.unlock();
    }
  };

  // ---------------------------------------------------------------------
  // Unlock, preferences, and state readout
  // ---------------------------------------------------------------------

  /** Idempotent - safe to call from every early primary-button gesture, never creates a second `AudioContext`. */
  unlock(): void {
    this.backend.unlock();
  }

  getEngineState(): AudioEngineState {
    return this.backend.getState();
  }

  onStateChange(cb: (state: AudioEngineState) => void): () => void {
    return this.backend.onStateChange(cb);
  }

  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  /** Persists the choice and takes effect immediately - muting stops every loop/pending one-shot and prevents new sounds; unmuting resumes only loops that are still tracked as active (nothing stale is ever queued or replayed). */
  setSoundEnabled(enabled: boolean): void {
    if (this.soundEnabled === enabled) return;
    this.soundEnabled = enabled;
    AudioPreferences.setSoundEnabled(enabled);
    if (!enabled) {
      this.clearPendingOneShots();
      this.backend.stopAll();
    } else if (!this.paused) {
      this.playOneShot("buttonClick", AUDIO_PRIORITY.ambient);
    }
  }

  private canPlay(): boolean {
    return this.soundEnabled && this.backend.getState() === "running";
  }

  // ---------------------------------------------------------------------
  // Pause / lifecycle
  // ---------------------------------------------------------------------

  onPauseChange(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.playOneShot(paused ? "pause" : "resume", AUDIO_PRIORITY.ambient);
  }

  /** New run / Retry / Replay (`scene.restart()`) - stops every pending one-shot, and resets every dedupe/warning-tier/countdown-tracking field so nothing from the previous run can leak into the next. */
  onRestart(): void {
    this.stopAll();
    this.prevWormholeRemainingMs = null;
    this.prevSpeedTrialRemainingMs = null;
    this.speedTrialEndedHandled = false;
    this.wormholeWarnTierMs = null;
    this.speedTrialWarnTierMs = null;
    this.starfruitGateWarnTierMs = null;
    this.starfruitPresent = false;
    this.prevFlowStreak = 0;
    this.paused = false;
  }

  /** Scene shutdown - removes every Scene-owned pending timer without destroying the persistent preference/unlock state (a new Scene reuses the same singleton). */
  onSceneShutdown(): void {
    this.stopAll();
  }

  /** Immediately silences every pending one-shot - the QA runtime's STOP ALL, and the shared core of restart/scene-shutdown. */
  stopAll(): void {
    this.clearPendingOneShots();
    this.backend.stopAll();
  }

  private clearPendingOneShots(): void {
    for (const active of this.activeOneShots) active.scheduler.stop();
    this.activeOneShots = [];
    for (const id of this.pendingTimeouts) clearTimeout(id);
    this.pendingTimeouts.clear();
  }

  // ---------------------------------------------------------------------
  // Gameplay events - one method per authoritative transition GameScene
  // already calls `VisualFeedbackController` from.
  // ---------------------------------------------------------------------

  onButtonClick(): void {
    this.playOneShot("buttonClick", AUDIO_PRIORITY.ambient);
  }

  onBiteEvent(event: BiteFeedbackEvent, ctx: BiteAudioContext = {}): void {
    switch (event.kind) {
      case "safe":
        this.playOneShot("safe", AUDIO_PRIORITY.ambient);
        return;
      case "rush": {
        // RUSH consistency fix (QA follow-up): every tier gets the exact
        // same priority - previously only ×4/×5 were bumped to
        // AUDIO_PRIORITY.skill while ×1-×3 stayed at the much weaker
        // AUDIO_PRIORITY.rush, so the identical nominal event (a RUSH bite)
        // had a different survival chance under MAX_POLYPHONY eviction
        // purely as a function of stack size - the root cause of "the RUSH
        // sound doesn't feel consistent". Tying every tier to skill's
        // priority means a same-tick credited maneuver can no longer
        // silently evict a lower-tier RUSH cue.
        const stack = Math.min(5, Math.max(1, Math.round(event.stack))) as 1 | 2 | 3 | 4 | 5;
        this.playOneShot(`rush${stack}` as SoundEventId, AUDIO_PRIORITY.skill);
        return;
      }
      case "starfruit":
        this.playOneShot("starfruitEaten", AUDIO_PRIORITY.majorEvent);
        this.playOneShot("energyTravel", AUDIO_PRIORITY.majorEvent);
        return;
      case "speed-trial-success":
        this.speedTrialEndedHandled = true;
        this.speedTrialWarnTierMs = null;
        this.playOneShot("speedTrialSuccess", AUDIO_PRIORITY.statusActivation);
        this.scheduleAfter("speedTrialSuccess", () => this.playOneShot("speedTrialExit", AUDIO_PRIORITY.statusActivation));
        return;
      case "chaos":
        this.onChaosOutcome(event, ctx);
        return;
      default:
        return;
    }
  }

  private onChaosOutcome(event: Extract<BiteFeedbackEvent, { kind: "chaos" }>, ctx: BiteAudioContext): void {
    switch (event.outcome) {
      case "jackpot":
        this.playOneShot("jackpot", AUDIO_PRIORITY.majorEvent);
        return;
      case "shrink":
        this.playOneShot("shrink", AUDIO_PRIORITY.majorEvent);
        return;
      case "score-hit":
        this.playOneShot("legacyScoreHit", AUDIO_PRIORITY.majorEvent);
        return;
      case "wormhole":
        // QA follow-up: WORMHOLE is silent while merely active - no
        // continuously repeating loop, only the distinct moment sounds
        // below (activation/refresh/wrap/one expiry warning/expiry,
        // WORMHOLE BITE/CLUTCH elsewhere via `onBiteBonus`).
        this.wormholeWarnTierMs = null;
        if (ctx.wormholeAlreadyActiveBefore) {
          this.playOneShot("wormholeRefresh", AUDIO_PRIORITY.statusActivation);
        } else {
          this.playOneShot("wormholeActivation", AUDIO_PRIORITY.statusActivation);
        }
        return;
      case "speed-trial":
        // QA follow-up: SPEED TRIAL is silent while merely active too - no
        // repeating loop, only this one ignition-style activation, the
        // single final warning, and success/exit below. A fresh SPEED
        // TRIAL can never roll while one is already active (no CHAOS bite
        // is even on the board mid-trial), so this never double-fires.
        this.speedTrialWarnTierMs = null;
        this.speedTrialEndedHandled = false;
        this.playOneShot("speedTrialStart", AUDIO_PRIORITY.statusActivation);
        return;
    }
  }

  onSkillMoment(moment: SkillMoment): void {
    const flowIncreased = moment.flowStreak > this.prevFlowStreak;
    this.prevFlowStreak = moment.flowStreak;
    if (moment.doubleDanger) {
      this.playOneShot("doubleDanger", AUDIO_PRIORITY.skill);
    } else if (moment.maneuvers.length > 0) {
      this.playOneShot("maneuver", AUDIO_PRIORITY.skill);
    } else if (flowIncreased) {
      this.playOneShot("flowIncrease", AUDIO_PRIORITY.skill);
    }
  }

  onBiteBonus(bonus: BiteBonusResult): void {
    const eventId: SoundEventId =
      bonus.source === "wormhole" ? (bonus.kind === "trick" ? "wormholeBite" : "wormholeClutch") : bonus.kind === "trick" ? "trickBite" : "clutchBite";
    this.playOneShot(eventId, AUDIO_PRIORITY.skill);
  }

  onWormholeWrap(): void {
    this.playOneShot("wormholeWrap", AUDIO_PRIORITY.majorEvent);
  }

  /** `isTemporaryGate` is the same authoritative check `GameScene` already has available (`snapshot.starfruitGateRemainingMs !== null` right after the gate opened) - true for STARFRUIT's temporary gate, false for the real permanent gate (including one opened by a completed SPEED TRIAL's reward growth). */
  onGateOpened(isTemporaryGate: boolean): void {
    this.playOneShot(isTemporaryGate ? "gateTempOpen" : "gatePermanentOpen", AUDIO_PRIORITY.majorEvent);
  }

  onGateEntry(): void {
    this.starfruitGateWarnTierMs = null;
    this.playOneShot("gateEntry", AUDIO_PRIORITY.majorEvent);
  }

  /** The temporary STARFRUIT gate closing on its own (real timeout) - never called for entry/promoted-to-permanent, which is what stops this from ever playing a false closure. */
  onStarfruitGateExpired(): void {
    this.starfruitGateWarnTierMs = null;
    this.playOneShot("gateTempClose", AUDIO_PRIORITY.majorEvent);
  }

  /** `present` is the same `bites.some(b => b.kind === "starfruit")` check `GameScene.trackStarfruitSpawn()` already computes - fires the appearance sound once per genuine spawn (absent -> present), never on every frame it stays present, and never twice if it respawns later in the same run. */
  onStarfruitPresenceChanged(present: boolean): void {
    if (present && !this.starfruitPresent) this.playOneShot("starfruitAppear", AUDIO_PRIORITY.majorEvent);
    this.starfruitPresent = present;
  }

  /** Stops pending warnings/scheduled follow-ups and clears every countdown-tracking field - shared by every way a run can end (death, a campaign level complete, or a Daily complete with no star/record concept of its own). */
  private resetForRunEnd(): void {
    this.clearPendingOneShots();
    this.wormholeWarnTierMs = null;
    this.speedTrialWarnTierMs = null;
    this.starfruitGateWarnTierMs = null;
    this.prevWormholeRemainingMs = null;
    this.prevSpeedTrialRemainingMs = null;
  }

  onDeath(): void {
    this.resetForRunEnd();
    this.playOneShot("death", AUDIO_PRIORITY.runEnd);
  }

  /** The L3 completion moment alone, with no star-fill/record accents - Daily Challenge has no star/best-score concept of its own, so its completion (unlike a campaign level complete) never has that data to play. */
  onLevelCompleteCore(): void {
    this.resetForRunEnd();
    this.playOneShot("levelComplete", AUDIO_PRIORITY.runEnd);
  }

  /**
   * The campaign completion phrase: the softened L3 core immediately
   * followed by AT MOST one award accent, never both NEW STAR RECORD and
   * NEW HIGH SCORE stacked and never a sound per star-fill tick (QA
   * follow-up - see `buildLevelSummaryAudio()`'s own doc for why). Star
   * count is deliberately not a parameter here any more - the audio side
   * never needed it for anything but the ordinary star-fill ticks this
   * follow-up removed; `VisualFeedbackController` still animates the
   * real star count from `GameScene`'s own data, untouched.
   */
  onLevelComplete(isNewStarRecord: boolean, isNewHighScore: boolean): void {
    this.resetForRunEnd();
    const combo: RecordCombo = isNewStarRecord && isNewHighScore ? "both" : isNewStarRecord ? "starRecord" : isNewHighScore ? "highScore" : "none";
    this.playLayers(buildLevelSummaryAudio(combo), AUDIO_PRIORITY.runEnd);
  }

  /** Per-frame continuous update - the audio-side twin of `VisualFeedbackController.update()`. Detects WORMHOLE/SPEED TRIAL natural expiry (there is no other authoritative signal for it - see the class doc) and fires the shared final-countdown warning tiers, purely from the same authoritative remaining-time fields the timer badges already read. Never fires a one-shot from re-rendering; every branch here only reacts to the CURRENT snapshot differing from the previous one. */
  update(_deltaMs: number, snapshot: AudioTimedSnapshot): void {
    if (this.paused) return;

    const wormholeTransition = classifyCountdown(this.prevWormholeRemainingMs, snapshot.wormholeRemainingMs);
    if (wormholeTransition === "ended") {
      this.playOneShot("wormholeExpiry", AUDIO_PRIORITY.statusActivation);
      this.wormholeWarnTierMs = null;
    }
    // QA follow-up: WORMHOLE and SPEED TRIAL each get exactly one
    // expiry/final warning (2s remaining), never the shared 3-2-1 ladder
    // the STARFRUIT gate still uses - see `SINGLE_EXPIRY_WARNING_TIER_MS`'s doc.
    this.checkWarningTier(snapshot.wormholeRemainingMs, "wormholeWarnTierMs", "wormholeWarningPulse", SINGLE_EXPIRY_WARNING_TIER_MS);
    this.prevWormholeRemainingMs = snapshot.wormholeRemainingMs;

    const speedTrialTransition = classifyCountdown(this.prevSpeedTrialRemainingMs, snapshot.speedTrialRemainingMs);
    if (speedTrialTransition === "ended") {
      if (!this.speedTrialEndedHandled) {
        this.playOneShot("speedTrialExit", AUDIO_PRIORITY.statusActivation);
      }
      this.speedTrialWarnTierMs = null;
      this.speedTrialEndedHandled = false;
    }
    this.checkWarningTier(snapshot.speedTrialRemainingMs, "speedTrialWarnTierMs", "speedTrialWarningPulse", SINGLE_EXPIRY_WARNING_TIER_MS);
    this.prevSpeedTrialRemainingMs = snapshot.speedTrialRemainingMs;

    this.checkWarningTier(snapshot.starfruitGateRemainingMs, "starfruitGateWarnTierMs", "starfruitGateWarning", WARNING_TIERS_MS);

    this.purgeFinishedOneShots();
  }

  private checkWarningTier(
    remainingMs: number | null,
    field: "wormholeWarnTierMs" | "speedTrialWarnTierMs" | "starfruitGateWarnTierMs",
    eventId: SoundEventId,
    tiers: readonly number[],
  ): void {
    if (remainingMs === null) {
      this[field] = null;
      return;
    }
    for (const tier of tiers) {
      const currentTier = this[field];
      if (remainingMs <= tier && (currentTier === null || currentTier > tier)) {
        this[field] = tier;
        this.playOneShot(eventId, AUDIO_PRIORITY.statusActivation);
        return;
      }
    }
  }

  // ---------------------------------------------------------------------
  // One-shot playback - dedupe/priority/polyphony
  // ---------------------------------------------------------------------

  private playOneShot(eventId: SoundEventId, priority: number): void {
    this.playLayers(FINAL_MAPPING[eventId].layers, priority, eventId);
  }

  private playLayers(layers: readonly CandidateLayer[], priority: number, debugEventId?: SoundEventId): void {
    if (!this.canPlay()) return;
    this.purgeFinishedOneShots();
    if (this.activeOneShots.length >= MAX_POLYPHONY) {
      this.activeOneShots.sort((a, b) => a.priority - b.priority);
      const lowest = this.activeOneShots[0];
      if (!lowest || lowest.priority > priority) return;
      lowest.scheduler.stop();
      this.activeOneShots.shift();
    }

    const scheduler = new SequenceScheduler();
    const active: ActiveOneShot = { scheduler, priority };
    this.activeOneShots.push(active);
    scheduler.play(
      layers,
      (layer) => {
        this.lastEventId = debugEventId ?? this.lastEventId;
        this.backend.playRecipe(layer.synth, layer.transposeSemitones !== undefined ? { transposeSemitones: layer.transposeSemitones } : undefined);
      },
      () => {
        this.activeOneShots = this.activeOneShots.filter((o) => o !== active);
      },
    );
  }

  private purgeFinishedOneShots(): void {
    this.activeOneShots = this.activeOneShots.filter((o) => o.scheduler.isPlaying);
  }

  /** Schedules a follow-up one-shot after `eventId`'s own natural duration - used only for SPEED TRIAL's Success -> Exit sequencing, which (unlike every other multi-layer moment) spans two separately-mapped `SoundEventId`s rather than one candidate's fixed layer list. */
  private scheduleAfter(eventId: SoundEventId, run: () => void): void {
    const durationMs = FINAL_MAPPING[eventId].layers.reduce((max, l) => Math.max(max, l.atMs + l.synth.durationMs), 0);
    const id = setTimeout(() => {
      this.pendingTimeouts.delete(id);
      run();
    }, durationMs);
    this.pendingTimeouts.add(id);
  }

  // ---------------------------------------------------------------------
  // QA runtime readout (production `?qa=audio` panel)
  // ---------------------------------------------------------------------

  getActiveVoiceCount(): number {
    return this.activeOneShots.length;
  }

  getLastEventId(): SoundEventId | null {
    return this.lastEventId;
  }

  /**
   * QA-only: plays `eventId`'s approved production recipe directly,
   * through the exact same dedupe/priority/polyphony pipeline every real
   * gameplay one-shot uses - not a second mock implementation. Used only
   * by the `?qa=audio` "PRODUCTION AUDIO RUNTIME" panel to let individual
   * events be auditioned on demand; never called from real gameplay code.
   */
  auditionEvent(eventId: SoundEventId): void {
    this.playOneShot(eventId, AUDIO_PRIORITY.majorEvent);
  }
}

let singleton: AudioManager | null = null;

/** The one production `AudioManager` instance for the page's lifetime - never recreated per Scene, so unlock state, the Sound On/Off preference, and any active loop survive a `scene.restart()`. */
export function getAudioManager(): AudioManager {
  if (!singleton) singleton = new AudioManager();
  return singleton;
}

/** Test-only escape hatch - replaces the singleton with a fresh instance (optionally over an injected fake backend) so each test starts from a clean, unlocked-nothing state. */
export function resetAudioManagerForTests(backend?: AudioBackendLike): AudioManager {
  singleton = new AudioManager(backend);
  return singleton;
}
